import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, User, ArrowLeft } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        toast.success('Login successful');
        navigate('/demos/beauty-salon/admin/dashboard');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <Button
          data-testid="back-to-home-admin-btn"
          variant="ghost"
          onClick={() => navigate('/demos/beauty-salon')}
          className="mb-8 gap-2"
        >
          <ArrowLeft size={20} weight="light" />
          Back to Home
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-background-paper rounded-md border border-border p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent-soft mb-4">
              <Lock size={32} weight="light" className="text-accent" />
            </div>
            <h2 className="text-2xl font-serif font-medium mb-2">Admin Login</h2>
            <p className="text-foreground-secondary text-sm">Access your dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="username" className="flex items-center gap-2 mb-2">
                <User size={18} weight="light" />
                Username
              </Label>
              <Input
                id="username"
                data-testid="admin-username-input"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="bg-background-subtle"
                placeholder="Enter username"
              />
            </div>

            <div>
              <Label htmlFor="password" className="flex items-center gap-2 mb-2">
                <Lock size={18} weight="light" />
                Password
              </Label>
              <Input
                id="password"
                data-testid="admin-password-input"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="bg-background-subtle"
                placeholder="Enter password"
              />
            </div>

            <Button
              data-testid="admin-login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-accent-soft rounded-md text-sm text-foreground-secondary">
            <p className="font-medium mb-1">Demo Credentials:</p>
            <p>Username: admin</p>
            <p>Password: admin123</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminLoginPage;